/** Converte 25/08/2026 → 2026-08-25. Devolve null se a data não estiver completa. */
export function brDateToIso(value?: string | null): string | null {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length !== 8) return null;

  return `${digits.slice(4, 8)}-${digits.slice(2, 4)}-${digits.slice(0, 2)}`;
}

/** Converte 2026-08-25 → 25/08/2026. */
export function isoToBrDate(value?: string | null): string {
  if (!value) return '';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  return year && month && day ? `${day}/${month}/${year}` : '';
}

/**
 * "2026-08-25" → Date à meia-noite local. Não usa `new Date(iso)` porque ISO
 * sem hora é interpretado como UTC — em fuso negativo (Brasil) isso volta um
 * dia quando exibido localmente.
 */
export function isoDateToDate(value?: string | null): Date | null {
  if (!value) return null;
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
}

/** Date local → "2026-08-25". Usa os componentes locais, não `toISOString` (mesmo motivo do desvio de fuso). */
export function dateToIsoDate(date?: Date | null): string | null {
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** "14:30" → Date de hoje com essa hora, para o DatePicker `timeOnly`. */
export function timeStringToDate(value?: string | null): Date | null {
  if (!value) return null;
  const [hours, minutes] = value.split(':').map(Number);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
}

/** Date → "14:30". */
export function dateToTimeString(date?: Date | null): string | null {
  if (!date) return null;
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatDate(iso?: string | null): string {
  return isoToBrDate(iso) || '—';
}

export function formatPhone(digits?: string | null): string {
  const value = String(digits ?? '').replace(/\D/g, '');
  if (value.length === 11) return value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (value.length === 10) return value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return value || '—';
}

export function formatPostalCode(digits?: string | null): string {
  const value = String(digits ?? '').replace(/\D/g, '');
  return value.length === 8 ? value.replace(/(\d{5})(\d{3})/, '$1-$2') : value || '';
}

/** "Rua Córrego do Ouro, 155, Apto 201, Alto José Bonifácio, Recife/PE, 52280-280" */
export function formatFullAddress(location?: {
  street?: string | null;
  number?: string | null;
  complement?: string | null;
  district?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}): string {
  if (!location) return '';

  const cityAndState =
    location.city && location.state ? `${location.city}/${location.state}` : location.city;

  return [
    location.street,
    location.number,
    location.complement,
    location.district,
    cityAndState,
    formatPostalCode(location.postalCode),
  ]
    .filter((part) => part && String(part).trim())
    .join(', ');
}
